import { createContext, useContext, type ReactNode } from 'react';
import { hardwarePathLabel, type HardwareConflict } from '@cer-autofill/shared';

export const HardwareConflictsContext = createContext<readonly HardwareConflict[]>([]);
const HardwareRowContext = createContext('');
export function HardwareConflictRow({
  path,
  children,
  className,
}: {
  path: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <HardwareRowContext.Provider value={path}>
      <fieldset className={className}>{children}</fieldset>
    </HardwareRowContext.Provider>
  );
}
export function ConflictHint({ path }: { path: string }) {
  const row = useContext(HardwareRowContext);
  const conflicts = useContext(HardwareConflictsContext);
  const conflict = conflicts.find(
    (c) => c.path === (row ? `${row}.${path}` : path) && !c.resolution,
  );
  return conflict ? (
    <a
      href="#hardware-conflicts"
      style={{
        display: 'block',
        color: '#92400e',
        background: '#fef3c7',
        padding: 6,
        borderRadius: 4,
      }}
    >
      Conflict: field says{' '}
      {Array.isArray(conflict.fieldValue)
        ? conflict.fieldValue.join(', ')
        : String(conflict.fieldValue)}{' '}
      · Review conflict
    </a>
  ) : null;
}
export function ConflictLabel({ path, children }: { path: string; children: ReactNode }) {
  const row = useContext(HardwareRowContext);
  const conflicts = useContext(HardwareConflictsContext);
  const conflict = conflicts.some(
    (c) => !c.resolution && c.path === (row ? `${row}.${path}` : path),
  );
  return (
    <label
      style={
        conflict
          ? { background: '#fffbeb', outline: '1px solid #d69e2e', padding: 8, borderRadius: 4 }
          : undefined
      }
    >
      {children}
      <ConflictHint path={path} />
    </label>
  );
}
/** Includes conflicts on schema leaves not otherwise exposed by the compact read view. */
export function HardwareConflictValues({ conflicts }: { conflicts: readonly HardwareConflict[] }) {
  return (
    <dl>
      {conflicts
        .filter((c) => !c.resolution)
        .map((c) => (
          <div
            key={c.id}
            style={{
              background: '#fffbeb',
              border: '1px solid #d69e2e',
              padding: 8,
              marginBlock: 8,
            }}
          >
            <dt>{hardwarePathLabel(c.path)}</dt>
            <dd>
              {Array.isArray(c.dashboardValue)
                ? c.dashboardValue.join(', ')
                : String(c.dashboardValue)}
              <ConflictHint path={c.path} />
            </dd>
          </div>
        ))}
    </dl>
  );
}

export function useHardwareConflict(path?: string) {
  const conflicts = useContext(HardwareConflictsContext);
  return conflicts.find((c) => !c.resolution && c.path === path);
}
